import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { useRefundOrder, type FoodpandaOrder } from './api';

/**
 * "Foodpanda refund": everything, or some items, back as a sales return with its credit note on
 * the Foodpanda invoice. Food goes back to stock only as the recipe's restock flag says.
 */
export function RefundDialog({ order, onClose }: { order: FoodpandaOrder; onClose: () => void }) {
  const { format } = useShop();
  const notify = useNotify();
  const refund = useRefundOrder();
  const invoiceId = order.invoice?.id ?? '';
  const invoice = useQuery({
    queryKey: ['sales-invoices', 'detail', invoiceId],
    queryFn: () => unwrap(api.GET('/sales-invoices/{id}', { params: { path: { id: invoiceId } } })),
    enabled: !!invoiceId,
  });
  const [scope, setScope] = useState<'all' | 'some'>('all');
  const [qty, setQty] = useState<Record<string, string>>({});
  const [condition, setCondition] = useState<'RESALABLE' | 'DAMAGED'>('RESALABLE');
  const [reason, setReason] = useState(order.cancelReason ?? '');
  const lines = invoice.data?.lines ?? [];
  const picked = lines.flatMap((l) => {
    const q = Number(qty[l.id] ?? 0);
    return q > 0 ? [{ invoiceLineId: l.id, qty: String(q) }] : [];
  });
  const ready = scope === 'all' || picked.length > 0;

  return (
    <Dialog open onClose={refund.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Foodpanda refund · #{order.code}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Records the refund Foodpanda gave the customer: a sales return and a credit note on{' '}
            {order.invoice?.number}, so Foodpanda owes you that much less.
          </Alert>
          <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as 'all' | 'some')}>
            <FormControlLabel value="all" control={<Radio />} label="Refund the whole order" />
            <FormControlLabel value="some" control={<Radio />} label="Refund some items" />
          </RadioGroup>
          {scope === 'some' &&
            lines.map((l) => (
              <Stack key={l.id} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {l.description} · {Number(l.qty)} × {money(l.unitPrice, format)}
                </Typography>
                <TextField
                  size="small"
                  label="Qty"
                  value={qty[l.id] ?? ''}
                  onChange={(e) =>
                    setQty((q) => ({ ...q, [l.id]: e.target.value.replace(/[^\d.]/g, '') }))
                  }
                  slotProps={{
                    htmlInput: { inputMode: 'decimal', 'aria-label': `Refund ${l.description}` },
                  }}
                  sx={{ width: 90 }}
                />
              </Stack>
            ))}
          <TextField
            select
            label="The goods"
            value={condition}
            onChange={(e) => setCondition(e.target.value as 'RESALABLE' | 'DAMAGED')}
            helperText="Recipe items follow their own “return to stock” setting."
          >
            <MenuItem value="RESALABLE">Back in stock</MenuItem>
            <MenuItem value="DAMAGED">Damaged or thrown away</MenuItem>
          </TextField>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={refund.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!ready || refund.isPending}
          onClick={() =>
            refund
              .mutateAsync({
                id: order.id,
                version: order.version,
                condition,
                reason: reason.trim() || null,
                ...(scope === 'some' ? { lines: picked } : {}),
              })
              .then(() => {
                notify.success('Refund recorded: return and credit note posted');
                onClose();
              }, notify.error)
          }
        >
          Record refund
        </Button>
      </DialogActions>
    </Dialog>
  );
}
